/**
 * 『いろはに咲きて』 (Irohani Sakite)
 * 作詞・作曲・編曲：samayuzame / MV映像：ぬヴェントス / タイポグラフィ：本忠学
 * Official MV: https://youtu.be/EiPZenIQZa0
 *
 * ジェネラティブアート・コンセプト（1シーン完結・歌詞表示なし・4つのMV世界観モード）:
 * - MV全編を徹底解析し、以下の象徴的な視覚モチーフを1シーン完結のインタラクティブ・ジェネラティブアートとして構築：
 *   - 紅と黒の斜め分割空間を走る「白い扇骨（和傘）の放射ストライプ細線」と、中央の「大きな円窓（丸窓）・三日月巴シャッター」（`irohani_maxres1.jpg` / `irohani_z1_top.png`）
 *   - 桃色の火雫（花雫）が落ちて弾け、世界を鮮烈な朱紅（バーミリオン）へと染め上げるラスサビの空間と、
 *     そこに浮かぶ「白いT字型の鉄骨高架舞台」および「黄色・水色・桃色・白の菱形紙吹雪（◆）と星粒」（`irohani_maxresdefault.jpg` / `irohani_maxres3.jpg` / `irohani_z3_bot.png`）
 *   - 純白の鉄骨高架回廊に絡みつく「白銀（シルバーブルー）の立体的な茨・棘の蔦」と、その先に咲く「淡桃色の丸い花毬（綿毛球）」（`irohani_maxres2.jpg` / `irohani_z2_top.png`）
 *   - 漆黒の闇に浮かぶ「深紅の円窓（紅月）」と扇骨ライン、空間を浮遊する「無数の赤い3D立方体」と結晶光糸（`irohani_z2_top.png` / `irohani_z2_bot.png`）
 * - 「🎲 転生（再生成）」ボタンを押すたびに、上記4つのMV空間モードが切り替わる。
 * - クリックすると「桃色の火雫」と「円窓三日月シャッター波紋・菱形紙吹雪」が弾けて咲き広がる。
 */

export class IrohaniGenerator {
  constructor(p) {
    this.p = p;
    this.time = 0;
    this.modeIndex = -1; // 初回initで決定、再生成で4つのMVモードを切り替え

    // 菱形紙吹雪（◆：黄・水色・桃・赤・白）と星粒（irohani_maxresdefault.jpg / irohani_maxres3.jpg）
    this.confettiDiamonds = [];
    // 白銀の立体茨（棘の蔦）と淡桃色の丸い花毬（irohani_maxres2.jpg）
    this.silverThornVines = [];
    // 浮遊する赤い3D立方体群（irohani_z2_bot.png）
    this.redCubes = [];
    // 落下して弾ける桃色の火雫（irohani_z3_bot.png）
    this.flameDrops = [];
    // クリックで広がる円窓・三日月巴シャッター波紋
    this.moonGateRipples = [];
    // カーソル移動軌跡の菱形＆火花トレイル
    this.cursorTrail = [];

    this.pointerX = 0;
    this.pointerY = 0;
    this.parallaxX = 0;
    this.parallaxY = 0;
    this.bloomPulse = 0;
  }

  onReroll() {
    this.modeIndex = (this.modeIndex + 1) % 4;
    this.bloomPulse = 1.3;
  }

  init(params) {
    const p = this.p;
    const w = p.width || 800;
    const h = p.height || 600;
    const density = params ? params.density : 0.5;
    const scale = Math.min(w, h) / 720;

    if (this.modeIndex < 0) {
      this.modeIndex = Math.floor(p.random(4));
    }

    this.pointerX = w * 0.5;
    this.pointerY = h * 0.5;
    this.confettiDiamonds = [];
    this.silverThornVines = [];
    this.redCubes = [];
    this.flameDrops = [];
    this.moonGateRipples = [];
    this.cursorTrail = [];

    // 1. 菱形紙吹雪（◆）と輝く星粒の生成（irohani_maxresdefault.jpg / irohani_maxres3.jpg）
    const confettiCount = Math.floor(65 + density * 75);
    const confettiColors = [
      [250, 224, 38],  // 鮮やかな黄色ダイヤ（右下等の象徴的モチーフ）
      [52, 192, 248],  // 水色（シアン）ダイヤ
      [246, 72, 148],  // 桃色（マゼンタ）ダイヤ
      [232, 28, 38],   // 紅（クリムゾン）ダイヤ
      [255, 252, 252]  // 純白の星粒・小ダイヤ
    ];

    for (let i = 0; i < confettiCount; i++) {
      const isStarDot = i % 3 !== 0;
      const col = isStarDot
        ? [255, 248, 248]
        : confettiColors[i % confettiColors.length];

      this.confettiDiamonds.push({
        x: p.random(w),
        y: p.random(h),
        vx: p.random(-0.55, 0.55),
        vy: p.random(-1.35, -0.35),
        size: isStarDot ? p.random(2.5, 5.5) * scale : p.random(7, 18) * scale,
        rot: p.random(p.TWO_PI),
        rotSpeed: p.random(-0.045, 0.045),
        tilt3D: p.random(p.TWO_PI),
        tiltSpeed: p.random(0.02, 0.06),
        col,
        isStarDot,
        phase: p.random(p.TWO_PI)
      });
    }

    // 2. 白銀（シルバーブルー）の立体茨と淡桃色の丸い花毬（irohani_maxres2.jpg / irohani_z2_top.png）
    const vineCount = Math.floor(6 + density * 5);
    for (let i = 0; i < vineCount; i++) {
      const side = i % 2 === 0 ? -1 : 1;
      const baseX = w * (0.5 + side * p.random(0.14, 0.44));
      const baseY = h * p.random(0.78, 0.96);
      const heightSpan = h * p.random(0.45, 0.78);
      this.silverThornVines.push({
        baseX,
        baseY,
        heightSpan,
        curveDir: -side * p.random(0.6, 1.3),
        thickness: p.random(9, 18) * scale,
        swayPhase: p.random(p.TWO_PI),
        swaySpeed: p.random(0.7, 1.3),
        hasPinkPuff: i % 2 === 0 || i < 3,
        puffRadius: p.random(38, 72) * scale,
        depth: p.random(0.5, 1.25)
      });
    }

    // 3. 浮遊する赤い3D立方体群（irohani_z2_bot.png Row 1-2）
    const cubeCount = Math.floor(14 + density * 16);
    for (let i = 0; i < cubeCount; i++) {
      this.redCubes.push({
        xRatio: p.random(0.06, 0.94),
        yRatio: p.random(0.14, 0.86),
        size: p.random(12, 30) * scale,
        rotX: p.random(p.TWO_PI),
        rotY: p.random(p.TWO_PI),
        rotSpeedX: p.random(-0.025, 0.025),
        rotSpeedY: p.random(-0.03, 0.03),
        floatSpeed: p.random(0.4, 1.0),
        phase: p.random(p.TWO_PI),
        depth: p.random(0.4, 1.3)
      });
    }

    // 4. 周期的に落ちてきて弾ける桃色の火雫（irohani_z3_bot.png Row 1）
    for (let i = 0; i < 3; i++) {
      this.flameDrops.push(this.createFlameDrop(w, h, scale, true));
    }
  }

  createFlameDrop(w, h, scale, randomY = false) {
    const p = this.p;
    return {
      x: p.random(w * 0.2, w * 0.8),
      y: randomY ? p.random(h * 0.1, h * 0.7) : -30 * scale,
      vy: p.random(2.4, 4.2) * scale,
      radius: p.random(14, 24) * scale,
      phase: p.random(p.TWO_PI),
      splashY: h * p.random(0.66, 0.82)
    };
  }

  onPointerDown(x, y) {
    const p = this.p;
    const scale = Math.min(p.width, p.height) / 720;

    this.bloomPulse = 1.4;

    // クリック位置に円窓・三日月巴シャッター波紋を生成
    this.moonGateRipples.push({
      x,
      y,
      radius: 14 * scale,
      maxRadius: p.random(125, 220) * scale,
      rot: p.random(p.TWO_PI),
      rotSpeed: (p.random() < 0.5 ? 1 : -1) * 0.035,
      alpha: 255
    });
    if (this.moonGateRipples.length > 6) {
      this.moonGateRipples.shift();
    }

    // 菱形紙吹雪（◆）と桃色火花のバースト
    const burstColors = [
      [250, 224, 38],
      [52, 192, 248],
      [246, 72, 148],
      [232, 28, 38],
      [255, 250, 252]
    ];
    for (let i = 0; i < 18; i++) {
      const ang = (p.TWO_PI / 18) * i + p.random(-0.15, 0.15);
      const spd = p.random(2.2, 6.8) * scale;
      this.confettiDiamonds.push({
        x,
        y,
        vx: Math.cos(ang) * spd,
        vy: Math.sin(ang) * spd,
        size: p.random(8, 18) * scale,
        rot: p.random(p.TWO_PI),
        rotSpeed: p.random(-0.08, 0.08),
        tilt3D: p.random(p.TWO_PI),
        tiltSpeed: p.random(0.05, 0.12),
        col: burstColors[i % burstColors.length],
        isStarDot: i % 4 === 0,
        phase: p.random(p.TWO_PI),
        temporary: true,
        alpha: 255
      });
    }
    if (this.confettiDiamonds.length > 220) {
      this.confettiDiamonds.splice(0, this.confettiDiamonds.length - 220);
    }
  }

  onPointerMove(x, y) {
    const p = this.p;
    const scale = Math.min(p.width, p.height) / 720;
    this.pointerX = x;
    this.pointerY = y;

    if (p.frameCount % 2 === 0) {
      const trailColors = [
        [250, 224, 38],
        [246, 72, 148],
        [52, 192, 248],
        [232, 28, 38]
      ];
      this.cursorTrail.push({
        x,
        y,
        size: p.random(7, 13) * scale,
        rot: p.random(p.TWO_PI),
        col: trailColors[p.frameCount % trailColors.length],
        alpha: 225
      });
      if (this.cursorTrail.length > 26) {
        this.cursorTrail.shift();
      }
    }
  }

  draw(params) {
    const p = this.p;
    const w = p.width;
    const h = p.height;
    const motion = params ? params.motion : 0.5;
    const glow = params ? params.glow : 0.6;
    const scale = Math.min(w, h) / 720;

    const dt = Math.min(p.deltaTime || 16.67, 50) / 16.67;
    const speedMult = 0.45 + motion * 1.15;
    this.time += 0.016 * dt * speedMult;
    this.bloomPulse = p.lerp(this.bloomPulse, 0, 0.06 * dt);

    // カーソル視差（パララックス）
    const targetPx = ((this.pointerX || w * 0.5) / w - 0.5) * 28;
    const targetPy = ((this.pointerY || h * 0.5) / h - 0.5) * 28;
    this.parallaxX = p.lerp(this.parallaxX, targetPx, 0.07);
    this.parallaxY = p.lerp(this.parallaxY, targetPy, 0.07);

    const cx = w * 0.5 - this.parallaxX * 0.45;
    const cy = h * 0.5 - this.parallaxY * 0.45;

    // =========================================================================
    // 再生成（Reroll）で切り替わる4つのMV空間モードの描画
    // =========================================================================
    if (this.modeIndex === 0) {
      // Mode 0: 『円窓と紅黒の扇骨ストライプ』（irohani_maxres1.jpg / irohani_z1_top.png Row 0）
      this.drawMode0CrimsonFanAndMoonGate(cx, cy, w, h, scale, dt, speedMult, glow);
    } else if (this.modeIndex === 1) {
      // Mode 1: 『朱紅の覚醒と桃色火雫・白き鉄骨高架舞台』（irohani_maxresdefault.jpg / irohani_maxres3.jpg / irohani_z3_bot.png）
      this.drawMode1VermilionSkyStageAndFlameSplash(cx, cy, w, h, scale, dt, speedMult, glow);
    } else if (this.modeIndex === 2) {
      // Mode 2: 『白銀の茨と淡桃色の花毬・純白の鉄骨高架回廊』（irohani_maxres2.jpg / irohani_z1_top.png Row 1-2）
      this.drawMode2WhiteBridgeAndSilverThorns(cx, cy, w, h, scale, dt, speedMult, glow);
    } else {
      // Mode 3: 『漆黒の紅月と扇骨・浮遊する赤い立方体と結晶光糸』（irohani_z2_top.png Row 1-2 / irohani_z2_bot.png）
      this.drawMode3BlackNightRedMoonAndCubes(cx, cy, w, h, scale, dt, speedMult, glow);
    }

    // =========================================================================
    // 共通インタラクティブ層：円窓三日月波紋・菱形紙吹雪（◆）・カーソルトレイル・上下黒帯
    // =========================================================================
    this.updateAndDrawMoonGateRipples(scale, dt);
    this.updateAndDrawConfettiDiamonds(w, h, scale, dt, speedMult);
    this.updateAndDrawCursorTrail(scale, dt);
    this.drawCinemascopeLetterbox(w, h);
  }

  /**
   * Mode 0: 『円窓と紅黒の扇骨ストライプ』（irohani_maxres1.jpg / irohani_z1_top.png Row 0 完全再現）
   */
  drawMode0CrimsonFanAndMoonGate(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;

    // 1. 深紅（#9c080c〜#b80e14）のベース背景
    p.background(168, 12, 18);

    p.push();
    // 2. 右側から斜めに切り込む漆黒の扇形バンド（irohani_maxres1.jpg の黒帯）
    p.noStroke();
    p.fill(16, 18, 22);
    p.beginShape();
    p.vertex(w * 0.46, h * 0.42);
    p.vertex(w * 0.84, 0);
    p.vertex(w, 0);
    p.vertex(w, h * 0.28);
    p.vertex(w * 0.52, h * 0.62);
    p.endShape(p.CLOSE);

    // 3. 右側から放射状に広がる「和傘・扇骨」の白い細線ストライプ群（irohani_maxres1.jpg の象徴的パターン）
    const fanOriginX = w * 0.28 - this.parallaxX * 0.3;
    const fanOriginY = h * 0.56 - this.parallaxY * 0.3;
    const rayCount = 46;
    p.stroke(238, 240, 245, 165);
    p.strokeWeight(1.15 * scale);
    for (let i = 0; i < rayCount; i++) {
      const a = p.map(i, 0, rayCount - 1, -0.78, 0.72) + Math.sin(this.time * 0.5 + i * 0.05) * 0.008;
      const rStart = 185 * scale;
      const rEnd = Math.max(w, h) * 1.15;
      p.line(
        fanOriginX + Math.cos(a) * rStart,
        fanOriginY + Math.sin(a) * rStart,
        fanOriginX + Math.cos(a) * rEnd,
        fanOriginY + Math.sin(a) * rEnd
      );
    }

    // 4. 左側深紅エリアの繊細なダイヤ格子と縦ライン＋3連白ドット（⋮）（irohani_maxres1.jpg 左側＆irohani_z1_top.png Row 0）
    p.stroke(245, 180, 185, 42);
    p.strokeWeight(0.9 * scale);
    const step = 46 * scale;
    for (let x = -h; x < w * 0.5; x += step) {
      p.line(x, 0, x + h * 0.6, h);
      p.line(x + h * 0.6, 0, x, h);
    }

    const lineX = w * 0.14 - this.parallaxX * 0.2;
    p.stroke(248, 250, 252, 210);
    p.strokeWeight(1.3 * scale);
    p.line(lineX, h * 0.08, lineX, h * 0.58);
    p.noStroke();
    p.fill(250, 252, 255, 240);
    for (let d = 0; d < 3; d++) {
      p.circle(lineX, h * (0.62 + d * 0.03), 5.5 * scale);
    }

    // 5. 中央の巨大な「円窓（丸窓）」と内側の三日月リム・扇骨リング・黒い三日月巴シャッター
    this.drawCentralMoonGateWindow(cx, cy, 242 * scale, scale, true);

    p.pop();
  }

  /**
   * 中央の円窓（丸窓）と三日月ハイライト・扇骨内輪・巴シャッターの描画
   * （irohani_maxres1.jpg & irohani_z1_top.png Row 0 & irohani_z2_top.png Row 1）
   */
  drawCentralMoonGateWindow(cx, cy, radius, scale, isSlateInterior) {
    const p = this.p;
    const r = radius + this.bloomPulse * 10 * scale;

    p.push();
    p.translate(cx, cy);

    // 外側のダークグレー縁リング
    p.noStroke();
    p.fill(24, 27, 32, 245);
    p.circle(0, 0, r * 2.08);

    // 内側の円窓空間（Mode 0ではスレートグレー、Mode 3では深紅の満月）
    if (isSlateInterior) {
      p.fill(118, 122, 128);
      p.circle(0, 0, r * 1.92);

      // 右側内縁のダークグレー扇骨リング帯（irohani_maxres1.jpg の円窓右側）
      p.fill(58, 62, 68);
      p.arc(0, 0, r * 1.92, r * 1.92, -p.HALF_PI * 0.85, p.HALF_PI * 0.85, p.PIE);

      // 円窓内部を走る放射状の白い細線（扇骨）
      p.stroke(235, 238, 242, 175);
      p.strokeWeight(1.1 * scale);
      for (let i = -18; i <= 18; i++) {
        const a = i * 0.048 + Math.sin(this.time * 0.6) * 0.02;
        p.line(Math.cos(a) * r * 0.56, Math.sin(a) * r * 0.56, Math.cos(a) * r * 0.96, Math.sin(a) * r * 0.96);
      }

      // 中央奥の明るいグレー円と三日月状の純白ハイライト（irohani_z1_top.png Row 0 Col 3-5）
      p.noStroke();
      p.fill(248, 250, 252);
      p.circle(r * 0.08, 0, r * 1.38);
      p.fill(138, 142, 148);
      p.circle(-r * 0.04, 0, r * 1.34);
    } else {
      // 深紅の円窓（irohani_z2_top.png Row 1）
      p.fill(186, 14, 22);
      p.circle(0, 0, r * 1.94);
      p.fill(225, 24, 34, 190);
      p.circle(r * 0.08, -r * 0.04, r * 1.48);
      p.fill(148, 10, 16);
      p.circle(-r * 0.06, r * 0.04, r * 1.36);
    }

    // 円窓の中で息づく白銀の茨と黒椿（花飾りモチーフの黒い花輪シルエット）
    this.drawStylizedBlackCamelliaInMoon(0, 0, r * 0.55, scale);

    // 円窓の外周を旋回する「黒い三日月巴（ともえ）シャッター羽根」（irohani_z1_top.png Row 0 Col 2 / irohani_z2_top.png Row 1 Col 3）
    for (let k = 0; k < 2; k++) {
      const bladeAngle = this.time * 0.45 + k * p.PI;
      p.push();
      p.rotate(bladeAngle);
      p.noStroke();
      p.fill(22, 24, 28, 230);
      p.beginShape();
      for (let t = 0; t <= 20; t++) {
        const u = t / 20;
        const a = u * p.PI * 1.15;
        const rad = r * (1.04 - u * 0.22);
        p.vertex(Math.cos(a) * rad, Math.sin(a) * rad);
      }
      for (let t = 20; t >= 0; t--) {
        const u = t / 20;
        const a = u * p.PI * 1.15;
        const rad = r * (0.86 - u * 0.08);
        p.vertex(Math.cos(a) * rad, Math.sin(a) * rad);
      }
      p.endShape(p.CLOSE);
      p.pop();
    }

    // 円窓のシャープな白銀内縁ライン
    p.noFill();
    p.stroke(242, 245, 250, 215);
    p.strokeWeight(1.8 * scale);
    p.circle(0, 0, r * 1.92);

    p.pop();
  }

  /**
   * ヰ世界情緒の髪飾り（黒い8枚花弁と紅い芯の椿／アネモネ花飾り）と銀の棘の象徴的モチーフ描画
   */
  drawStylizedBlackCamelliaInMoon(x, y, radius, scale) {
    const p = this.p;
    p.push();
    p.translate(x, y);
    p.rotate(this.time * 0.18);

    // 背後の繊細な銀の棘リング
    p.noFill();
    p.stroke(235, 240, 248, 170);
    p.strokeWeight(1.3 * scale);
    p.circle(0, 0, radius * 1.65);

    // 8枚の漆黒の花弁（白い縁取りハイライト付き）
    const petals = 8;
    for (let i = 0; i < petals; i++) {
      const a = (p.TWO_PI / petals) * i;
      p.push();
      p.rotate(a);
      p.fill(18, 20, 24, 242);
      p.stroke(225, 230, 238, 185);
      p.strokeWeight(1.1 * scale);
      p.beginShape();
      p.vertex(0, 0);
      p.bezierVertex(radius * 0.35, -radius * 0.38, radius * 0.85, -radius * 0.32, radius * 0.96, 0);
      p.bezierVertex(radius * 0.85, radius * 0.32, radius * 0.35, radius * 0.38, 0, 0);
      p.endShape(p.CLOSE);
      p.pop();
    }

    // 中心の白銀リングと深紅の瞳芯（irohani_maxres1.jpg / irohani_maxres2.jpg の髪飾り）
    p.noStroke();
    p.fill(235, 238, 244, 245);
    p.circle(0, 0, radius * 0.46);
    p.fill(22, 24, 28, 250);
    p.circle(0, 0, radius * 0.34);
    p.fill(205, 18, 26, 245);
    p.ellipse(0, radius * 0.03, radius * 0.24, radius * 0.16);

    p.pop();
  }

  /**
   * Mode 1: 『朱紅の覚醒と桃色火雫・白き鉄骨高架舞台』
   * （irohani_maxresdefault.jpg / irohani_maxres3.jpg / irohani_z3_bot.png Row 1-2 / irohani_z4_top.png 完全再現）
   */
  drawMode1VermilionSkyStageAndFlameSplash(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;

    // 1. ラスサビの鮮烈な朱紅（バーミリオン：#d81612〜#e81e16）の宇宙背景
    p.background(218, 20, 18);

    // 中央の柔らかい朱色の発光グラデーション
    p.push();
    p.noStroke();
    for (let r = 5; r >= 1; r--) {
      p.fill(242, 42, 32, (6 - r) * 14 * glow);
      p.circle(cx, cy - 20 * scale, r * 145 * scale);
    }

    // 2. 下部地平でうねる「桃紅色の流体スプラッシュリボン」（irohani_z3_bot.png Row 1 Col 4 & irohani_z4_top.png Row 1）
    this.drawPinkLiquidSplashHorizon(w, h, scale);

    // 3. 朱紅の空間に浮かび上がる「純白のT字型・鉄骨高架舞台」（irohani_z3_bot.png Row 2 Col 3-4）
    this.drawWhiteTBridgeStage(cx, cy + 55 * scale, scale, true);

    // 4. 空から落ちてきて水面で弾ける「桃色の火雫（花雫）」（irohani_z3_bot.png Row 1 Col 1-3）
    this.updateAndDrawFlameDrops(w, h, scale, dt);

    p.pop();
  }

  /**
   * 俯瞰の「純白T字型・鉄骨高架舞台（展望デッキ＋下部鉄骨トラス構造）」の描画
   * （irohani_z1_top.png Row 2 Col 3-4 & irohani_z3_bot.png Row 2 Col 3-4 完全再現）
   */
  drawWhiteTBridgeStage(cx, cy, scale, isOnRedBg) {
    const p = this.p;
    p.push();
    p.translate(cx, cy);

    const deckW = 310 * scale;
    const deckD = 125 * scale;
    const walkwayW = 72 * scale;
    const walkwayL = 260 * scale;

    // 1. ステージ下を支える精緻な3D鉄骨トラス柱群（irohani_z1_top.png Row 2 / irohani_z3_bot.png Row 2）
    const trussH = 185 * scale;
    const colCount = 7;
    p.stroke(isOnRedBg ? [255, 225, 225, 165] : [145, 155, 172, 185]);
    p.strokeWeight(1.2 * scale);
    p.noFill();

    for (let i = 0; i < colCount; i++) {
      const tx = p.map(i, 0, colCount - 1, -deckW * 0.48, deckW * 0.48);
      const ty = deckD * 0.35 + (1 - Math.abs(i - 3) * 0.08) * 14 * scale;
      // 垂直柱
      p.line(tx, ty, tx, ty + trussH);
      if (i < colCount - 1) {
        const nx = p.map(i + 1, 0, colCount - 1, -deckW * 0.48, deckW * 0.48);
        const ny = deckD * 0.35 + (1 - Math.abs(i + 1 - 3) * 0.08) * 14 * scale;
        // 水平梁とX字ブレース（鉄骨トラス）
        for (let lv = 1; lv <= 4; lv++) {
          const y1 = ty + (trussH / 4) * (lv - 1);
          const y2 = ty + (trussH / 4) * lv;
          const ny1 = ny + (trussH / 4) * (lv - 1);
          const ny2 = ny + (trussH / 4) * lv;
          p.line(tx, y2, nx, ny2);
          p.line(tx, y1, nx, ny2);
          p.line(nx, ny1, tx, y2);
        }
      }
    }

    // 2. 奥へと伸びるT字の連絡橋（白い高架回廊）
    p.fill(252, 252, 255, 248);
    p.stroke(isOnRedBg ? [255, 255, 255, 255] : [120, 130, 148, 220]);
    p.strokeWeight(1.8 * scale);
    p.beginShape();
    p.vertex(-walkwayW * 0.5, -deckD * 0.35);
    p.vertex(walkwayW * 0.5, -deckD * 0.35);
    p.vertex(walkwayW * 0.38 + 65 * scale, -deckD * 0.35 - walkwayL);
    p.vertex(-walkwayW * 0.38 + 65 * scale, -deckD * 0.35 - walkwayL);
    p.endShape(p.CLOSE);

    // 3. 手前の広い長方形展望デッキ床面（アイソメトリック台形）
    p.fill(254, 254, 255, 252);
    p.beginShape();
    p.vertex(-deckW * 0.46, -deckD * 0.35);
    p.vertex(deckW * 0.46, -deckD * 0.35);
    p.vertex(deckW * 0.54, deckD * 0.38);
    p.vertex(-deckW * 0.54, deckD * 0.38);
    p.endShape(p.CLOSE);

    // 4. ステージ周囲の白い手すり（欄干レールと支柱ポスト）
    const railH = 16 * scale;
    p.stroke(isOnRedBg ? [235, 195, 200, 225] : [135, 146, 165, 225]);
    p.strokeWeight(1.3 * scale);
    // 手前レール（2段）
    p.line(-deckW * 0.54, deckD * 0.38 - railH, deckW * 0.54, deckD * 0.38 - railH);
    p.line(-deckW * 0.54, deckD * 0.38 - railH * 0.5, deckW * 0.54, deckD * 0.38 - railH * 0.5);
    for (let k = 0; k <= 8; k++) {
      const px = p.map(k, 0, 8, -deckW * 0.54, deckW * 0.54);
      p.line(px, deckD * 0.38, px, deckD * 0.38 - railH);
    }

    p.pop();
  }

  /**
   * 桃紅色の流体スプラッシュリボン（irohani_z3_bot.png Row 1 Col 4 / irohani_z4_top.png Row 1）
   */
  drawPinkLiquidSplashHorizon(w, h, scale) {
    const p = this.p;
    p.push();
    p.noStroke();

    for (let layer = 0; layer < 2; layer++) {
      p.fill(layer === 0 ? [248, 82, 148, 175] : [255, 155, 205, 145]);
      p.beginShape();
      p.vertex(0, h);
      for (let x = 0; x <= w; x += 24) {
        const nx = x / w;
        const wave =
          Math.sin(nx * 6.5 - this.time * 2.2 + layer) * 28 * scale +
          Math.cos(nx * 13.0 + this.time * 1.7) * 14 * scale;
        // 中央付近で王冠スプラッシュのように高く跳ね上がる造形
        const crownSpike = Math.exp(-Math.pow((nx - 0.5) * 5.5, 2)) * (68 + this.bloomPulse * 35) * scale;
        p.vertex(x, h * (0.82 + layer * 0.03) + wave - crownSpike);
      }
      p.vertex(w, h);
      p.endShape(p.CLOSE);
    }
    p.pop();
  }

  /**
   * 落下して弾ける「桃色の火雫（花雫）」（irohani_z3_bot.png Row 1 Col 1-3）
   */
  updateAndDrawFlameDrops(w, h, scale, dt) {
    const p = this.p;
    for (let i = 0; i < this.flameDrops.length; i++) {
      const fd = this.flameDrops[i];
      fd.y += fd.vy * dt;

      if (fd.y >= fd.splashY) {
        // 着水位置で小さな波紋を生んで再生成
        this.moonGateRipples.push({
          x: fd.x,
          y: fd.splashY,
          radius: 8 * scale,
          maxRadius: 85 * scale,
          rot: p.random(p.TWO_PI),
          rotSpeed: 0.03,
          alpha: 210
        });
        if (this.moonGateRipples.length > 6) this.moonGateRipples.shift();
        this.flameDrops[i] = this.createFlameDrop(w, h, scale, false);
        continue;
      }

      // 炎のような尾を引く桃白の発光雫を描画
      p.push();
      p.translate(fd.x, fd.y);
      p.noStroke();
      const r = fd.radius;
      // 外側のマゼンタピンク炎オーラ
      p.fill(252, 95, 168, 195);
      p.beginShape();
      p.vertex(0, -r * 2.1);
      p.bezierVertex(r * 1.1, -r * 0.6, r * 1.2, r * 0.8, 0, r * 1.1);
      p.bezierVertex(-r * 1.2, r * 0.8, -r * 1.1, -r * 0.6, 0, -r * 2.1);
      p.endShape(p.CLOSE);

      // 内側の白桃色コア
      p.fill(255, 232, 245, 245);
      p.ellipse(0, r * 0.15, r * 0.95, r * 1.15);
      p.pop();
    }
  }

  /**
   * Mode 2: 『白銀の茨と淡桃色の花毬・純白の鉄骨高架回廊』
   * （irohani_maxres2.jpg / irohani_z1_top.png Row 1-2 / irohani_z2_top.png Row 0 完全再現）
   */
  drawMode2WhiteBridgeAndSilverThorns(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;

    // 1. 純白の明るい空間背景（#fafbfd）
    p.background(250, 251, 254);

    // 2. 背景に浮遊する半透明の桃色・水色プリズム三角形（irohani_sb0.jpg Row 2 Col 4-5）
    p.push();
    p.noStroke();
    p.fill(246, 175, 208, 95);
    p.triangle(cx - 195 * scale, cy + 35 * scale, cx - 105 * scale, cy - 95 * scale, cx - 65 * scale, cy + 48 * scale);
    p.fill(168, 224, 245, 95);
    p.triangle(cx + 85 * scale, cy - 65 * scale, cx + 195 * scale, cy - 15 * scale, cx + 125 * scale, cy + 75 * scale);
    p.pop();

    // 3. 背後の純白T字型・鉄骨高架舞台（白背景用のシルバーブルー陰影ライン）
    this.drawWhiteTBridgeStage(cx, cy + 65 * scale, scale, false);

    // 4. 手前に立ち上がる「白銀（シルバーブルー）の立体的な茨・棘の蔦」と「淡桃色の丸い花毬」（irohani_maxres2.jpg）
    this.drawSilverThornsAndPinkBlossomPuffs(w, h, scale);
  }

  /**
   * 白銀（シルバーブルー）の立体茨と淡桃色の丸い花毬（綿毛球）の描画（irohani_maxres2.jpg）
   */
  drawSilverThornsAndPinkBlossomPuffs(w, h, scale) {
    const p = this.p;

    for (let i = 0; i < this.silverThornVines.length; i++) {
      const v = this.silverThornVines[i];
      const bx = v.baseX - this.parallaxX * v.depth;
      const by = v.baseY - this.parallaxY * 0.4 * v.depth;
      const sway = Math.sin(this.time * v.swaySpeed + v.swayPhase) * 18 * scale;

      const tipX = bx + v.curveDir * 95 * scale + sway;
      const tipY = by - v.heightSpan;
      const ctrlX1 = bx - v.curveDir * 45 * scale;
      const ctrlY1 = by - v.heightSpan * 0.35;
      const ctrlX2 = bx + v.curveDir * 115 * scale + sway * 0.6;
      const ctrlY2 = by - v.heightSpan * 0.72;

      // 1. 先端に咲く「淡桃色の丸い花毬（綿毛球）」（irohani_maxres2.jpg の左上・右上の桃色球体花）
      if (v.hasPinkPuff) {
        this.drawPinkBlossomPuff(tipX, tipY, v.puffRadius, scale);
      }

      // 2. 金属光沢と氷青色の陰影を持つ白銀の茨茎（irohani_maxres2.jpg 右側の鋭い銀の棘）
      const steps = 16;
      for (let s = 0; s < steps; s++) {
        const t1 = s / steps;
        const t2 = (s + 1) / steps;
        const x1 = p.bezierPoint(bx, ctrlX1, ctrlX2, tipX, t1);
        const y1 = p.bezierPoint(by, ctrlY1, ctrlY2, tipY, t1);
        const x2 = p.bezierPoint(bx, ctrlX1, ctrlX2, tipX, t2);
        const y2 = p.bezierPoint(by, ctrlY1, ctrlY2, tipY, t2);

        const w1 = v.thickness * (1.0 - t1 * 0.65);
        // ダークスレートブルーの影面
        p.stroke(112, 124, 142, 235);
        p.strokeWeight(w1);
        p.line(x1, y1, x2, y2);

        // シルバーホワイトのハイライト稜線
        p.stroke(236, 242, 250, 245);
        p.strokeWeight(w1 * 0.45);
        p.line(x1 - 2 * scale, y1, x2 - 2 * scale, y2);

        // 節ごとに突き出す鋭い白銀の棘（トゲ）
        if (s > 2 && s < steps - 2 && s % 3 === 0) {
          const thornSide = (s + i) % 2 === 0 ? 1 : -1;
          const thornLen = (28 - t1 * 12) * scale;
          const tx = x1 + thornSide * thornLen;
          const ty = y1 - thornLen * 0.65;

          p.noStroke();
          p.fill(128, 138, 156, 235);
          p.triangle(x1, y1 - w1 * 0.5, x1, y1 + w1 * 0.5, tx, ty);
          p.fill(240, 245, 252, 245);
          p.triangle(x1, y1 - w1 * 0.5, x1, y1, tx, ty);
        }
      }
    }
  }

  /**
   * 淡桃色のふわふわした丸い花毬（綿毛球）の描画（irohani_maxres2.jpg）
   */
  drawPinkBlossomPuff(x, y, radius, scale) {
    const p = this.p;
    p.push();
    p.translate(x, y);
    p.noStroke();

    // 柔らかい淡桃色のグラデーション層
    for (let r = 6; r >= 1; r--) {
      const ratio = r / 6;
      const lumR = p.lerp(245, 236, ratio);
      const lumG = p.lerp(220, 185, ratio);
      const lumB = p.lerp(235, 208, ratio);
      p.fill(lumR, lumG, lumB, (7 - r) * 36);
      p.circle(0, 0, radius * 2 * ratio);
    }

    // 表面の繊細な花弁・綿毛テクスチャ線
    p.stroke(255, 242, 248, 165);
    p.strokeWeight(1.0 * scale);
    for (let k = 0; k < 18; k++) {
      const a = (p.TWO_PI / 18) * k + this.time * 0.15;
      const r1 = radius * 0.55;
      const r2 = radius * 0.96;
      p.line(Math.cos(a) * r1, Math.sin(a) * r1, Math.cos(a) * r2, Math.sin(a) * r2);
    }

    p.pop();
  }

  /**
   * Mode 3: 『漆黒の紅月と扇骨・浮遊する赤い立方体と結晶光糸』
   * （irohani_z2_top.png Row 1-2 & irohani_z2_bot.png Row 0-2 完全再現）
   */
  drawMode3BlackNightRedMoonAndCubes(cx, cy, w, h, scale, dt, speedMult, glow) {
    const p = this.p;

    // 1. 漆黒の背景（#0b0c10）
    p.background(11, 12, 16);

    p.push();
    // 2. 左上と右下から放射状に広がる「白い扇骨ストライプ細線」（irohani_z2_top.png Row 2 Col 1-2）
    p.stroke(230, 234, 242, 145);
    p.strokeWeight(1.05 * scale);
    for (let i = 0; i < 32; i++) {
      const a1 = p.map(i, 0, 31, 0.05, p.HALF_PI - 0.05);
      p.line(0, h * 0.07, Math.cos(a1) * w * 0.52, h * 0.07 + Math.sin(a1) * h * 0.48);

      const a2 = p.map(i, 0, 31, p.PI + 0.05, p.PI + p.HALF_PI - 0.05);
      p.line(w, h * 0.93, w + Math.cos(a2) * w * 0.52, h * 0.93 + Math.sin(a2) * h * 0.48);
    }

    // 3. 背後の白い斜め構造ビームと幾何学ワイヤーフレーム線（irohani_z2_top.png Row 1）
    p.noStroke();
    p.fill(242, 244, 248, 235);
    p.beginShape();
    p.vertex(0, h * 0.72);
    p.vertex(w * 0.42, h * 0.52);
    p.vertex(w * 0.42, h * 0.64);
    p.vertex(0, h * 0.85);
    p.endShape(p.CLOSE);

    // 4. 間奏のシアン・ライラック結晶光糸ネットワーク（irohani_z2_bot.png Row 0-1）
    p.noFill();
    for (let k = 0; k < 7; k++) {
      const isCyan = k % 2 === 0;
      p.stroke(isCyan ? [72, 215, 248, 95 * glow] : [225, 135, 248, 95 * glow]);
      p.strokeWeight(1.2 * scale);
      p.beginShape();
      for (let x = 0; x <= w; x += 45) {
        const y = cy + Math.sin(x * 0.008 + this.time * 1.4 + k * 0.9) * 140 * scale;
        p.vertex(x, y);
      }
      p.endShape();
    }

    // 5. 中央に浮かぶ「深紅の円窓（紅月）」と三日月巴シャッター（irohani_z2_top.png Row 1-2）
    this.drawCentralMoonGateWindow(cx, cy, 220 * scale, scale, false);

    // 6. 空間を浮遊する「無数の赤い3D立方体」（irohani_z2_bot.png Row 1-2）
    for (let i = 0; i < this.redCubes.length; i++) {
      const cb = this.redCubes[i];
      cb.rotX += cb.rotSpeedX * dt;
      cb.rotY += cb.rotSpeedY * dt;

      const x = cb.xRatio * w - this.parallaxX * cb.depth;
      const y = cb.yRatio * h + Math.sin(this.time * cb.floatSpeed + cb.phase) * 14 * scale - this.parallaxY * cb.depth;
      this.drawRed3DCube(x, y, cb.size, cb.rotX, cb.rotY, scale);
    }

    p.pop();
  }

  /**
   * 立体陰影つきの「赤い3D立方体」の描画（irohani_z2_bot.png Row 1-2）
   */
  drawRed3DCube(x, y, size, rotX, rotY, scale) {
    const p = this.p;
    p.push();
    p.translate(x, y);
    p.rotate(rotX * 0.6);

    const s = size;
    const ox = Math.cos(rotY) * s * 0.42;
    const oy = Math.sin(rotY) * s * 0.42;

    p.stroke(255, 165, 175, 195);
    p.strokeWeight(0.9 * scale);

    // 上面・側面（明るい朱赤）
    p.fill(238, 32, 42, 230);
    p.quad(-s * 0.5, -s * 0.5, s * 0.5, -s * 0.5, s * 0.5 + ox, -s * 0.5 + oy, -s * 0.5 + ox, -s * 0.5 + oy);

    // 右側面（深い紅）
    p.fill(158, 12, 20, 230);
    p.quad(s * 0.5, -s * 0.5, s * 0.5 + ox, -s * 0.5 + oy, s * 0.5 + ox, s * 0.5 + oy, s * 0.5, s * 0.5);

    // 正面（鮮やかなクリムゾン）
    p.fill(212, 18, 28, 240);
    p.rectMode(p.CENTER);
    p.rect(0, 0, s, s);

    p.pop();
  }

  /**
   * クリックで広がる「円窓・三日月巴シャッター波紋」の更新と描画
   */
  updateAndDrawMoonGateRipples(scale, dt) {
    const p = this.p;
    for (let i = this.moonGateRipples.length - 1; i >= 0; i--) {
      const rp = this.moonGateRipples[i];
      rp.radius = p.lerp(rp.radius, rp.maxRadius, 0.08 * dt);
      rp.rot += rp.rotSpeed * dt;
      rp.alpha -= 3.4 * dt;

      if (rp.alpha <= 0) {
        this.moonGateRipples.splice(i, 1);
        continue;
      }

      p.push();
      p.translate(rp.x, rp.y);
      p.rotate(rp.rot);
      p.noFill();

      // 円窓リングと扇骨ティック
      p.stroke(250, 245, 248, rp.alpha * 0.85);
      p.strokeWeight(1.8 * scale);
      p.circle(0, 0, rp.radius * 2);

      p.stroke(235, 28, 38, rp.alpha * 0.9);
      p.strokeWeight(2.6 * scale);
      p.arc(0, 0, rp.radius * 1.72, rp.radius * 1.72, 0, p.PI * 0.85);
      p.arc(0, 0, rp.radius * 1.72, rp.radius * 1.72, p.PI, p.PI * 1.85);

      p.pop();
    }
  }

  /**
   * 舞い上がる「菱形紙吹雪（◆：黄・水色・桃・赤・白）」と「輝く星粒」の更新・描画
   * （irohani_maxresdefault.jpg / irohani_maxres3.jpg 完全再現）
   */
  updateAndDrawConfettiDiamonds(w, h, scale, dt, speedMult) {
    const p = this.p;
    const isWhiteBg = this.modeIndex === 2;

    for (let i = this.confettiDiamonds.length - 1; i >= 0; i--) {
      const cd = this.confettiDiamonds[i];
      cd.x += (cd.vx + Math.sin(this.time * 1.6 + cd.phase) * 0.45) * dt;
      cd.y += cd.vy * speedMult * dt;
      cd.rot += cd.rotSpeed * dt;
      cd.tilt3D += cd.tiltSpeed * dt;

      if (cd.temporary) {
        cd.vx *= 0.95;
        cd.vy *= 0.95;
        cd.alpha -= 4.2 * dt;
        if (cd.alpha <= 0) {
          this.confettiDiamonds.splice(i, 1);
          continue;
        }
      } else {
        if (cd.y < -20) {
          cd.y = h + 20;
          cd.x = p.random(w);
        }
        if (cd.x < -20) cd.x = w + 20;
        if (cd.x > w + 20) cd.x = -20;
      }

      const alpha = cd.temporary ? cd.alpha : 235;
      p.push();
      p.translate(cd.x, cd.y);
      p.rotate(cd.rot);

      if (cd.isStarDot) {
        // 輝く星粒ドット（白背景の時は深紅またはシアンの粒にする）
        p.noStroke();
        if (isWhiteBg) {
          p.fill(220, 24, 38, alpha * 0.85);
        } else {
          p.fill(cd.col[0], cd.col[1], cd.col[2], alpha);
        }
        p.circle(0, 0, cd.size);
      } else {
        // 3D回転する菱形（◆）紙吹雪（irohani_maxresdefault.jpg 右下の黄色ダイヤ等）
        const wScale = Math.max(0.22, Math.abs(Math.cos(cd.tilt3D)));
        const dw = cd.size * 1.15 * wScale;
        const dh = cd.size * 0.72;
        p.noStroke();
        p.fill(cd.col[0], cd.col[1], cd.col[2], alpha);
        p.quad(0, -dh, dw, 0, 0, dh, -dw, 0);
      }

      p.pop();
    }
  }

  /**
   * カーソル移動軌跡の菱形（◆）トレイル描画
   */
  updateAndDrawCursorTrail(scale, dt) {
    const p = this.p;
    if (this.cursorTrail.length === 0) return;

    p.push();
    p.noStroke();
    for (let i = this.cursorTrail.length - 1; i >= 0; i--) {
      const tr = this.cursorTrail[i];
      tr.alpha -= 7.2 * dt;
      tr.rot += 0.06 * dt;
      if (tr.alpha <= 0) {
        this.cursorTrail.splice(i, 1);
        continue;
      }

      p.push();
      p.translate(tr.x, tr.y);
      p.rotate(tr.rot);
      p.fill(tr.col[0], tr.col[1], tr.col[2], tr.alpha);
      const s = tr.size;
      p.quad(0, -s * 0.65, s, 0, 0, s * 0.65, -s, 0);
      p.pop();
    }
    p.pop();
  }

  /**
   * MV特有のシネマスコープ上下黒帯（レターボックス）
   */
  drawCinemascopeLetterbox(w, h) {
    const p = this.p;
    const barH = Math.round(h * 0.072);
    p.push();
    p.noStroke();
    p.fill(5, 6, 8, 255);
    p.rect(0, 0, w, barH);
    p.rect(0, h - barH, w, barH);
    p.pop();
  }
}
